// Streaming Chat API Service
// Configure the base URL via REACT_APP_CHAT_API_BASE_URL; endpoint remains /chat/stream.
import { notifySessionUnauthorized } from "../auth/sessionUnauthorizedEvents";
import { authedFetch } from "../auth/authedFetch";
import { debug } from "../utils/debugLogger";

export const CHAT_API_BASE_URL = (process.env.REACT_APP_CHAT_API_BASE_URL || "").trim();

const CHAT_API_URL = CHAT_API_BASE_URL
  ? `${CHAT_API_BASE_URL.replace(/\/$/, "")}/chat/stream`
  : "";

debug.log(
  `Using Chat API URL (${process.env.REACT_APP_ENV || "production"}):`,
  CHAT_API_URL || "(missing env: REACT_APP_CHAT_API_BASE_URL)"
);

export interface StreamingChatRequest {
  agent_id: string;
  provider: string;
  messages: Array<{
    role: "user" | "assistant";
    content: string | Array<{
      type: "input_text" | "input_image" | "input_file";
      text?: string;
      image_url?: string;
      filename?: string;
      file_data?: string;
    }>;
  }>;
  customer_id: string;
  
  // Conversation persistence
  thread_id?: string;
  conversation_id?: string;
  
  model?: string;
  vector_store_id?: string;
  tool_preference?: string;
  suggestion_mode?: string;
  chip_id?: string;
}

/**
 * Sends a message to the streaming chat API and returns the Response object for SSE streaming
 * @param requestBody The chat request body
 * @param idToken The Cognito ID token for authorization
 * @returns Promise<Response> The fetch Response object for streaming
 */
export const sendStreamingChatMessage = async (
  requestBody: StreamingChatRequest,
  idToken: string,
  signal?: AbortSignal
): Promise<Response> => {
  try {
    if (!CHAT_API_URL) {
      debug.error(
        "Chat API URL is not configured. Set REACT_APP_CHAT_API_BASE_URL (base) or REACT_APP_CHAT_API_URL (full url)."
      );
      throw new Error(
        "Chat API URL is not configured. Please set REACT_APP_CHAT_API_BASE_URL or REACT_APP_CHAT_API_URL."
      );
    }

    if (!idToken) {
      throw new Error("idToken is required for streaming chat requests");
    }

    const response = await authedFetch(
      CHAT_API_URL,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        signal,
      },
      idToken
    );

    if (!response.ok) {
      if (response.status === 402) {
        const err = new Error("Insufficient credits") as Error & { chatStreamError: ChatStreamError };
        err.chatStreamError = {
          type: "INSUFFICIENT_CREDITS",
          message: "You've run out of credits. Add funds to continue chatting.",
          actionUrl: "/billing",
          actionText: "Add Credits",
        };
        throw err;
      }
      notifySessionUnauthorized(response.status);
      const errorText = await response.text();
      throw new Error(
        `HTTP ${response.status}: ${response.statusText} - ${errorText}`
      );
    }

    return response;
  } catch (error) {
    debug.error("Error sending streaming chat message:", error);
    throw error;
  }
};

/**
 * Checks the health status of the chat API
 * @param idToken The Cognito ID token for authorization
 * @returns Promise<boolean> True if health check passes (200), false otherwise
 */
export const checkChatHealth = async (idToken: string): Promise<boolean> => {
  try {
    if (!CHAT_API_BASE_URL) {
      debug.error("Chat API base URL is not configured");
      return false;
    }

    const healthUrl = `${CHAT_API_BASE_URL.replace(/\/$/, "")}/health`;
    
    const response = await authedFetch(
      healthUrl,
      {
        method: "GET",
      },
      idToken
    );

    if (response.status === 401 || response.status === 403) {
      notifySessionUnauthorized(response.status);
    }

    return response.status === 200;
  } catch (error) {
    debug.error("Health check error:", error);
    return false;
  }
};

export interface ChatStreamError {
  type: "BRAND_DETAILS_REQUIRED" | "INSUFFICIENT_CREDITS" | "GENERIC";
  message: string;
  actionUrl?: string;
  actionText?: string;
}

/**
 * Parses chat stream error data and returns a structured error object
 * @param errorData The error data from the stream
 * @returns Structured error object with type and display information
 */
export const parseChatStreamError = (errorData: any): ChatStreamError => {
  // Check for brand details required error
  if (
    errorData.code === "BRAND_DETAILS_REQUIRED" ||
    errorData.reason === "brand_details_required"
  ) {
    return {
      type: "BRAND_DETAILS_REQUIRED",
      message: "You need to update brand details page to chat with content creator agent. Please complete Brand Name + Website + Service Area + Brand Voice.",
      actionUrl: "/dashboard",
      actionText: "Go to Dashboard",
    };
  }

  // Generic error
  return {
    type: "GENERIC",
    message: errorData.message || errorData.code || "An error occurred",
  };
};
